import React, { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBuilding,
  faChartLine,
  faChevronLeft,
  faChevronRight,
  faPowerOff,
  faSackDollar,
  faTicket,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { PATH_MAP } from "../../configuration/PathMap"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useResellerLogged } from "../../context/useResellerContext"
import { useGetResellerProfile } from "../../hooks/Reseller/useGetResellerProfile"
import logo from "../../assets/images/Logo2.png"
import { DEFAULT_RESELLER_AVATAR } from "../../utils/resellerAvatar"
import { ResellerDashboardPage, ResellerDashboardSection } from "./ResellerDashboardPage"
import { PlatformNoticeBell } from "../../components/PlatformNotice/PlatformNoticeBell"
import { ERequestType } from "../../Enum/ERequestType"

const sectionOptions: {
  key: ResellerDashboardSection
  labelKey: string
  descriptionKey: string
  icon: IconDefinition
}[] = [
  {
    key: "overview",
    labelKey: "dashboard.sections.overview.label",
    descriptionKey: "dashboard.sections.overview.description",
    icon: faChartLine,
  },
  {
    key: "clients",
    labelKey: "dashboard.sections.clients.label",
    descriptionKey: "dashboard.sections.clients.description",
    icon: faBuilding,
  },
  {
    key: "commissions",
    labelKey: "dashboard.sections.commissions.label",
    descriptionKey: "dashboard.sections.commissions.description",
    icon: faSackDollar,
  },
  {
    key: "coupons",
    labelKey: "dashboard.sections.coupons.label",
    descriptionKey: "dashboard.sections.coupons.description",
    icon: faTicket,
  },
]

const isValidSection = (value: string | null): value is ResellerDashboardSection =>
  sectionOptions.some((item) => item.key === value)

const RESELLER_SIDEBAR_COLLAPSED_STORAGE_KEY = "reseller_sidebar_collapsed"

export const ResellerDashboardScreen: React.FC = () => {
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()

  const { t } = useTranslation("reseller")

  const sectionFromUrl = searchParams.get("section")

  const section: ResellerDashboardSection = isValidSection(sectionFromUrl) ? sectionFromUrl : "overview"

  useEffect(() => {
    if (isValidSection(sectionFromUrl)) return

    setSearchParams({ section: "overview" }, { replace: true })
  }, [sectionFromUrl, setSearchParams])

  const { resellerProfile, setResellerProfile } = useResellerLogged()

  const { handleGetResellerProfile } = useGetResellerProfile()

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => localStorage.getItem(RESELLER_SIDEBAR_COLLAPSED_STORAGE_KEY) === "1",
  )

  useEffect(() => {
    handleGetResellerProfile(setResellerProfile)
  }, [])

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(RESELLER_SIDEBAR_COLLAPSED_STORAGE_KEY, next ? "1" : "0")
      return next
    })
  }

  const handleLogout = () => {
    localStorage.removeItem(API_ROUTES.TOKEN_RESELLER)

    setResellerProfile(null)

    navigate(PATH_MAP.RESELLER_LOGIN_PAGE, { replace: true })
  }

  const handleSectionChange = (nextSection: ResellerDashboardSection) => {
    setSearchParams({ section: nextSection })
  }

  const currentSection = sectionOptions.find((item) => item.key === section)

  if (!resellerProfile) {
    return null
  }

  return (
    <div className="bg-white md:flex">
      <aside
        className={`sticky top-0 z-30 hidden h-dvh shrink-0 flex-col border-r border-slate-200 bg-white md:flex ${
          isSidebarCollapsed ? "md:w-16" : "md:w-64"
        }`}
      >
        <div
          className={`flex items-center gap-2 border-b border-slate-200 px-3 py-4 ${
            isSidebarCollapsed ? "justify-center" : "justify-between"
          }`}
        >
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              <img src={logo} className="w-10 shrink-0" alt="Logo" />

              <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">
                {t("dashboard.panel_title")}
              </span>
            </div>
          )}

          <button
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            onClick={handleToggleSidebar}
            title={t(isSidebarCollapsed ? "dashboard.expand_sidebar" : "dashboard.collapse_sidebar")}
            data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.handleToggleSidebar"
          >
            <FontAwesomeIcon icon={isSidebarCollapsed ? faChevronRight : faChevronLeft} className="text-xs" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
          <ul className="flex flex-col gap-1">
            {sectionOptions.map((item) => (
              <li key={item.key}>
                <button
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold transition ${
                    isSidebarCollapsed ? "justify-center" : ""
                  } ${
                    section === item.key
                      ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow"
                      : "text-slate-600 hover:bg-orange-50 hover:text-orange-700"
                  }`}
                  onClick={() => handleSectionChange(item.key)}
                  title={isSidebarCollapsed ? t(item.labelKey) : undefined}
                  data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.handleSectionChangeItemKey"
                >
                  <FontAwesomeIcon icon={item.icon} className="shrink-0 text-sm" />

                  {!isSidebarCollapsed && <span className="min-w-0 flex-1 truncate text-left">{t(item.labelKey)}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-slate-200 p-2">
          <button
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-red-700 transition hover:bg-red-50 ${
              isSidebarCollapsed ? "justify-center" : ""
            }`}
            onClick={handleLogout}
            title={isSidebarCollapsed ? t("dashboard.logout_button") : undefined}
            data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.logout"
          >
            <FontAwesomeIcon icon={faPowerOff} className="shrink-0 text-xs" />

            {!isSidebarCollapsed && (
              <span className="min-w-0 flex-1 truncate text-left">{t("dashboard.logout_button")}</span>
            )}
          </button>
        </div>
      </aside>

      <div className="min-h-screen min-w-0 flex-1 px-1 py-2 pb-32 md:px-4 md:pb-6 xl:px-6 2xl:px-8">
        <header className="sticky top-0 z-10 shrink-0 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-xl md:px-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {currentSection && (
                <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-red-100 text-orange-600 md:flex">
                  <FontAwesomeIcon icon={currentSection.icon} />
                </span>
              )}

              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold text-slate-900 md:text-xl">
                  {currentSection ? t(currentSection.labelKey) : t("dashboard.panel_title")}
                </h1>

                <p className="truncate text-xs text-slate-500 md:text-sm">
                  {currentSection ? t(currentSection.descriptionKey) : ""}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <PlatformNoticeBell requestType={ERequestType.Reseller} />

              <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1 pl-1 pr-3 md:flex">
                <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full bg-white">
                  <img src={DEFAULT_RESELLER_AVATAR} alt="" className="h-full w-full object-cover" />
                </span>

                <span className="max-w-[140px] truncate text-sm font-bold text-slate-800">{resellerProfile.name}</span>
              </div>

              <button
                className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 md:hidden"
                onClick={handleLogout}
                data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.logout"
              >
                <FontAwesomeIcon icon={faPowerOff} className="text-xs" />
                {t("dashboard.logout_button")}
              </button>
            </div>
          </div>
        </header>

        <main className="mt-2 md:mt-6">
          <ResellerDashboardPage section={section} />
        </main>

        <nav className="fixed bottom-0 left-0 right-0 z-20 border-t border-slate-200 bg-white px-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] pt-3 shadow-[0_-8px_28px_rgba(15,23,42,0.10)] md:hidden">
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-max items-center gap-2.5">
              {sectionOptions.map((item) => {
                const isActive = section === item.key

                return (
                  <button
                    key={item.key}
                    className={`inline-flex min-h-12 items-center gap-2.5 rounded-2xl border px-4 py-2.5 text-sm font-extrabold tracking-[0.01em] transition ${
                      isActive
                        ? "border-transparent bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md"
                        : "border-slate-200 bg-white text-slate-700 shadow-sm"
                    }`}
                    onClick={() => handleSectionChange(item.key)}
                    data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.handleSectionChangeItemKey2"
                  >
                    <FontAwesomeIcon icon={item.icon} className="text-sm" />

                    <span className="whitespace-nowrap">{t(item.labelKey)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </nav>
      </div>
    </div>
  )
}
